<script lang="ts" setup>
import { computed, ComputedRef } from 'vue';
import { useI18n } from '@/i18n/i18n';
import { CampaignProspect, CampaignActionKey, CampaignConditionKey } from '@/domains/models/Campaign';
import moment from 'moment/min/moment-with-locales';
import { addProspectActionLabels } from '@/utils/campaignActions';

const props = defineProps<{
  prospect: CampaignProspect
}>();

const { t } = useI18n();

const stepNameTranslations: Record<CampaignActionKey, string> = {
  SEND_MAIL: 'campaigns.steps.send-email',
  LINKEDIN_MESSAGE: 'campaigns.steps.linkedin-message',
  LINKEDIN_INMAIL_SALES_NAVIGATOR: 'campaigns.steps.linkedin-inmail-sales-navigator',
  LINKEDIN_INMAIL_RECRUITER: 'campaigns.steps.linkedin-inmail-recruiter',
  LINKEDIN_INMAIL_PREMIUM: 'campaigns.steps.linkedin-inmail-premium',
  LINKEDIN_INVITATION: 'campaigns.steps.linkedin-invitation',
  LINKEDIN_PROFILE_VISIT: 'campaigns.steps.linkedin-profile-visit',
  LINKEDIN_VOICE_NOTE: 'campaigns.steps.linkedin-voice-note'
};

const currentProspectAction = computed(() => {
  return props.prospect.prospectActions.find(action => action._campaignAction === props.prospect._currentAction);
});

const currentStepName = computed(() => {
  if (props.prospect.status === 'FINISHED') {
    return t('campaigns.next-action.finished');
  }
  if (!props.prospect._currentAction) {
    return t('campaigns.next-action.in-preparation');
  }

  const labeledActions = addProspectActionLabels(props.prospect.prospectActions, (campaignAction) => {
    return t(stepNameTranslations[campaignAction.type] || '');
  });

  const currentAction = labeledActions.find(pA => pA._campaignAction === props.prospect._currentAction);
  return currentAction?.campaignAction.label || '';
});

const stepStatus = computed(() => {
  if (props.prospect.status === 'FINISHED') {
    return {
      validationIcon: { icon: 'check', color: 'text-green-500' },
      tooltipText: ''
    };
  }

  if (!props.prospect._currentAction) {
    return {
      executionIcon: { icon: 'play', color: 'text-gray-400' },
      tooltipText: t('campaigns.next-action.retrieving-information')
    };
  }

  const action = currentProspectAction.value;
  if (!action) return {};

  if (action.failedAt) {
    if (action.executedAt) {
      return {
        executionIcon: { icon: 'play', color: 'text-green-500' },
        validationIcon: { icon: 'cross', color: 'text-red-500' },
        tooltipText: `${t('campaigns.next-action.validation-failed')} ${moment(action.failedAt).fromNow()}`
      };
    }
    return {
      executionIcon: { icon: 'cross', color: 'text-red-500' },
      tooltipText: action.error?.status && typeof action.error?.status === 'number'  ? `Erreur ${action.error?.status} :<br/> ${action.error?.title}` : 'Une erreur inattendue est survenue<br/>Veuillez contacter le support'
    };
  }

  if (action.executedAt) {
    return {
      executionIcon: { icon: 'play', color: 'text-green-500' },
      validationIcon: { icon: 'check', color: 'text-gray-400' },
      tooltipText: `Exécuté - ${t('campaigns.next-action.awaiting-validation')}`
    };
  }

  if (action.status === 'ONGOING') {
    return {
      executionIcon: { icon: 'play', color: 'text-gray-400' },
      tooltipText: t((actionStatusTranslations.ONGOING as Record<string, string>)[action.campaignAction.type] || '')
    };
  }

  if (action.status === 'PENDING') {
    return {
      executionIcon: { icon: 'play', color: 'text-gray-400' },
      validationIcon: { icon: 'check', color: 'text-gray-400' },
      tooltipText: t('campaigns.next-action.pending')
    };
  }

  return {
    executionIcon: { icon: 'play-circle', color: 'text-gray-400' },
    validationIcon: { icon: 'check', color: 'text-gray-400' },
    tooltipText: ''
  };
});

const actionStatusTranslations: Record<string, Record<string, string>> = {
  ONGOING: {
    LINKEDIN_MESSAGE: 'campaigns.next-action.awaiting-linkedin-message-send',
    LINKEDIN_INMAIL_SALES_NAVIGATOR: 'campaigns.next-action.awaiting-inmail-send',
    LINKEDIN_INMAIL_RECRUITER: 'campaigns.next-action.awaiting-inmail-send',
    LINKEDIN_INMAIL_PREMIUM: 'campaigns.next-action.awaiting-inmail-send',
    LINKEDIN_INVITATION: 'campaigns.next-action.awaiting-invitation-send',
    LINKEDIN_PROFILE_VISIT: 'campaigns.next-action.awaiting-profile-visit-send',
    LINKEDIN_VOICE_NOTE: 'campaigns.next-action.awaiting-voice-message-send',
    SEND_MAIL: 'campaigns.next-action.awaiting-email-send'
  }
};
</script>

<template>
  <div class="flex justify-start min-w-0">
    <p v-tooltip="currentStepName + '<br/>' + stepStatus.tooltipText"
      class="flex min-w-0 rounded-2xl bg-blue-100 px-2.5 h-5 text-xs text-blue-800 truncate items-center gap-1">
      <span class="truncate flex items-center mr-1">{{ currentStepName }}</span>
      <span v-if="stepStatus.executionIcon"
        :class="[stepStatus.executionIcon.color, `icon-${stepStatus.executionIcon.icon}`, 'flex items-center shrink-0']"></span>
      <span v-if="stepStatus.validationIcon"
        :class="[stepStatus.validationIcon.color, `icon-${stepStatus.validationIcon.icon}`, 'flex items-center shrink-0']"></span>
    </p>
  </div>
</template> 